import type { LucideProps } from 'lucide-react';

import type { ComponentType, Ref } from 'react';

export interface MemberActionRowProps {
  icon: ComponentType<LucideProps>;
  label: string;
  onClick: () => void;
  /** Muted line under the label. */
  description?: string;
  disabled?: boolean;
  title?: string;
  testId?: string;
  /** Red treatment — the leave-group row. */
  tone?: 'primary' | 'destructive';
  /** Swaps the icon for a spinner while the action runs. */
  loading?: boolean;
  /** Set when the row is a disclosure, so the state is announced. */
  expanded?: boolean;
  /** Id of the panel this row discloses. */
  controls?: string;
  /** Id of a longer explanation kept out of the accessible name. */
  describedBy?: string;
  ref?: Ref<HTMLButtonElement>;
}

const TONES = {
  primary: {
    disc: 'bg-primary-50 dark:bg-primary-500/10',
    icon: 'text-primary-500 dark:text-primary-400',
    label: 'text-primary-600 dark:text-primary-400',
  },
  destructive: {
    disc: 'bg-red-50 dark:bg-red-500/10',
    icon: 'text-red-500 dark:text-red-400',
    label: 'text-red-600 dark:text-red-400',
  },
} as const;

/**
 * An action styled as a member row, so the roster card reads as one list
 * instead of a stack of buttons and cards (mirrors the mobile Members tab).
 */
export function MemberActionRow({
  icon: Icon,
  label,
  onClick,
  description,
  disabled = false,
  title,
  testId,
  tone = 'primary',
  loading = false,
  expanded,
  controls,
  describedBy,
  ref,
}: MemberActionRowProps) {
  const colors = TONES[tone];

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      aria-expanded={expanded}
      aria-controls={expanded ? controls : undefined}
      aria-describedby={describedBy}
      className="w-full flex items-center text-left px-6 py-3 sm:py-4 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
    >
      <span
        className={`flex items-center justify-center w-10 h-10 mr-3 shrink-0 rounded-full ${colors.disc}`}
      >
        {loading ? (
          <div className="loading-spinner-dark w-4 h-4"></div>
        ) : (
          <Icon className={`w-5 h-5 ${colors.icon}`} />
        )}
      </span>
      <span className="min-w-0">
        <span
          className={`block text-sm sm:text-base font-semibold ${colors.label}`}
        >
          {label}
        </span>
        {description && (
          <span className="block mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {description}
          </span>
        )}
      </span>
    </button>
  );
}

export default MemberActionRow;
